import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { format, parseISO } from 'date-fns';
import sendMessageToFirestore from '../../utils/sendMessageToFirestore';
import { listenMessages } from '../../utils/firebaseListeners';
import { MdContacts, MdAttachFile, MdImage, MdVideocam, MdClose } from "react-icons/md";
import { GiSoundWaves } from "react-icons/gi";
import { PlusIcon, PhotoIcon, SparklesIcon, ClockIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { uploadFileWithCompression, isValidMmsFile, getFileTypeDisplay, formatFileSize } from '../../utils/fileUpload';
import MediaGallery from './MediaGallery';

export default function MessagePanel({ conversation, userId, onNavigateToContact, onNavigateToVoiceDialer }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [attachedMedia, setAttachedMedia] = useState([]);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentMenuRef = useRef(null);
  
  // Format phone number for display
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Strip any non-digit characters from the phone number
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Format the number according to US convention if it's 10 digits
    if (cleanNumber.length === 10) {
      return `(${cleanNumber.substring(0, 3)}) ${cleanNumber.substring(3, 6)}-${cleanNumber.substring(6)}`;
    } else if (cleanNumber.length === 11 && cleanNumber.startsWith('1')) {
      // Format the number according to US convention with country code
      return `+1 (${cleanNumber.substring(1, 4)}) ${cleanNumber.substring(4, 7)}-${cleanNumber.substring(7)}`;
    }
    
    // Return the original format if it doesn't match known patterns
    return phoneNumber;
  };

  // Handle click outside attachment menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(event.target)) {
        setShowAttachmentMenu(false);
      }
    };

    if (showAttachmentMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAttachmentMenu]);

  // Listen for messages with the real-time listener
  useEffect(() => {
    // Reset state
    setLoading(true);
    setError(null);
    setMessages([]);
    
    // Make sure we have a valid conversation and userId
    if (!conversation || !userId) {
      console.log('No conversation or userId provided');
      setLoading(false);
      return;
    }
    
    // Validate required fields in the conversation object
    if (!conversation.aliasNumber) {
      console.error('Conversation is missing aliasNumber');
      setError('Invalid conversation: missing phone number');
      setLoading(false);
      return;
    }
    
    if (!conversation.lastContactNumber) {
      console.error('Conversation is missing lastContactNumber');
      setError('Invalid conversation: missing contact number');
      setLoading(false);
      return;
    }
    
    console.log('Setting up message listener for conversation:', {
      aliasNumber: conversation.aliasNumber,
      lastContactNumber: conversation.lastContactNumber
    });
    
    // Set up the listener
    const unsubscribe = listenMessages(
      userId,
      conversation.aliasNumber,
      conversation.lastContactNumber,
      (newMessages) => {
        console.log(`Received ${newMessages.length} messages from listener`);
        setMessages(newMessages);
        setLoading(false);
        
        // Scroll to bottom after loading messages
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    );
    
    // Clean up on unmount or when conversation changes
    return () => unsubscribe();
  }, [conversation, userId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle file attachment
  const handleFileAttachment = async (event) => {
    const files = Array.from(event.target.files);
    setError(null);
    
    for (const file of files) {
      // Validate file with improved validation
      const validation = isValidMmsFile(file);
      if (!validation.isValid) {
        setError(`${file.name}: ${validation.error}`);
        continue;
      }
      
      try {
        // Create a preview URL for immediate display
        const previewUrl = URL.createObjectURL(file);
        
        // Create pending attachment entry with more details
        const pendingId = Date.now() + Math.random();
        const pendingAttachment = {
          id: pendingId,
          file,
          previewUrl,
          mediaUrl: null, // Will be set after upload
          type: file.type.startsWith('image/') ? 'image' : 'video',
          name: file.name,
          size: formatFileSize(file.size),
          uploading: true,
          uploadProgress: 0
        };
        
        setAttachedMedia(prev => [...prev, pendingAttachment]);
        
        // Upload file in background with compression for images
        uploadFileWithCompression(file, userId, {
          compress: true,
          maxWidth: 1920,
          quality: 0.8
        }).then(mediaUrl => {
          setAttachedMedia(prev => 
            prev.map(media => 
              media.id === pendingId 
                ? { ...media, mediaUrl, uploading: false, uploadProgress: 100 }
                : media
            )
          );
          console.log(`Successfully uploaded ${file.name} to Firebase Storage`);
        }).catch(error => {
          console.error('Upload failed:', error);
          setError(`Failed to upload ${file.name}: ${error.message}`);
          // Remove failed upload from attachments
          setAttachedMedia(prev => prev.filter(media => media.id !== pendingId));
        });
        
      } catch (error) {
        console.error('Error processing file:', error);
        setError(`Error processing ${file.name}: ${error.message}`);
      }
    }
    
    setShowAttachmentMenu(false);
    event.target.value = ''; // Reset file input
  };

  // Remove attached media
  const removeAttachedMedia = (id) => {
    setAttachedMedia(prev => {
      const updated = prev.filter(media => media.id !== id);
      // Clean up preview URLs
      const removed = prev.find(media => media.id === id);
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return updated;
    });
  };

  // Function to send new message
  const sendMessage = async (e) => {
    e.preventDefault();
    
    if ((!newMessage.trim() && attachedMedia.length === 0) || !conversation || !userId) return;
    
    try {
      setSending(true);
      setError(null);
      
      // Validate the phone numbers before sending
      if (!conversation.lastContactNumber) {
        throw new Error('Please enter a recipient phone number');
      }

      if (!conversation.aliasNumber) {
        throw new Error('Sender phone number is missing');
      }
      
      // Check if any media is still uploading
      const uploadingMedia = attachedMedia.filter(media => media.uploading);
      if (uploadingMedia.length > 0) {
        setError('Please wait for all media files to finish uploading');
        return;
      }
      
      // Extract media URLs for MMS (only include successfully uploaded media)
      const mediaUrls = attachedMedia
        .filter(media => media.mediaUrl && !media.uploading)
        .map(media => media.mediaUrl);
      
      // Add debug logging
      console.log('Sending message with data:', {
        to: conversation.lastContactNumber,
        from: conversation.aliasNumber,
        textLength: newMessage.trim().length,
        mediaCount: mediaUrls.length,
        userId: userId
      });
      
      // Send message to Firestore instead of Telnyx API
      await sendMessageToFirestore(
        userId,
        conversation.aliasNumber,
        conversation.lastContactNumber,
        newMessage.trim(),
        setNewMessage,
        mediaUrls
      );
      
      // Clear attached media
      attachedMedia.forEach(media => {
        if (media.previewUrl) {
          URL.revokeObjectURL(media.previewUrl);
        }
      });
      setAttachedMedia([]);
      
      // No need to manually add the message to the array
      // The real-time listener will update the UI automatically
      
      // Scroll to the bottom after sending
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Render AI Assistant Insight
  const renderAIInsight = (message) => {
    return (
      <div className="bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 rounded-xl p-4 backdrop-blur-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-1.5 rounded-lg bg-purple-600/30 border border-purple-500/50">
            <SparklesIcon className="h-4 w-4 text-purple-400" />
          </div>
          <span className="text-sm font-medium text-purple-400">AI Assistant Call</span>
          {message.callDuration && (
            <div className="flex items-center gap-1 text-xs text-white/60 ml-auto">
              <ClockIcon className="h-3 w-3" />
              {Math.floor(message.callDuration / 60)}:{(message.callDuration % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>
        
        {/* Call Summary */}
        {message.summary && (
          <div className="mb-3">
            <h4 className="text-sm font-medium text-white/90 mb-1">Call Summary:</h4>
            <p className="text-sm text-white/70 bg-white/5 rounded-lg p-2 border border-white/10">
              {message.summary}
            </p>
          </div>
        )}
        
        {/* Key Insights */}
        {message.insights && message.insights.length > 0 && (
          <div className="mb-3">
            <h4 className="text-sm font-medium text-white/90 mb-2">Key Insights:</h4>
            <div className="space-y-1">
              {message.insights.map((insight, index) => (
                <div key={index} className="text-xs text-white/70 bg-white/5 rounded-lg p-2 border border-white/10 flex items-start gap-2">
                  <span className="text-purple-400 font-medium">•</span>
                  <span>{insight}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Transcript Toggle */}
        {message.transcript && (
          <details className="group">
            <summary className="text-xs text-purple-400 cursor-pointer hover:text-purple-300 transition-colors mb-2 flex items-center gap-1">
              <span>View Full Transcript</span>
              <svg className="w-3 h-3 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="text-xs text-white/60 bg-black/20 rounded-lg p-3 border border-white/10 max-h-40 overflow-y-auto">
              <pre className="whitespace-pre-wrap font-mono">{message.transcript}</pre>
            </div>
          </details>
        )}
        
        {/* Assistant ID Badge */}
        {message.assistantId && (
          <div className="mt-3 pt-2 border-t border-white/10">
            <span className="text-xs text-white/50">Assistant ID: {message.assistantId}</span>
          </div>
        )}
      </div>
    );
  };

  // Render media content for MMS messages
  const renderMediaContent = (message) => {
    // Handle multiple possible media URL field names and formats
    let mediaUrls = [];
    
    // Check for different possible field names
    if (message?.mediaUrls && Array.isArray(message.mediaUrls)) {
      mediaUrls = message.mediaUrls;
    } else if (message?.media_urls && Array.isArray(message.media_urls)) {
      mediaUrls = message.media_urls;
    } else if (message?.metadata?.raw?.payload?.media && Array.isArray(message.metadata.raw.payload.media)) {
      // Extract from Telnyx webhook format
      mediaUrls = message.metadata.raw.payload.media.map(media => media.url || media).filter(url => url);
    } else if (message?.metadata?.mediaUrls && Array.isArray(message.metadata.mediaUrls)) {
      mediaUrls = message.metadata.mediaUrls;
    }
    
    // Debug logging - more detailed for both inbound and outbound
    if (message && Object.keys(message).length > 0) {
      console.log('Message data for media rendering:', {
        id: message.id,
        direction: message.direction,
        hasMediaUrls: !!message.mediaUrls,
        hasMedia_urls: !!message.media_urls,
        hasMetadata: !!message.metadata,
        metadataKeys: message.metadata ? Object.keys(message.metadata) : [],
        foundMediaUrls: mediaUrls
      });
      
      // Special debugging for outbound messages
      if (message.direction === 'outbound') {
        console.log('🔍 OUTBOUND MESSAGE DEBUG:', {
          messageId: message.id,
          hasMediaUrls: !!message.mediaUrls,
          mediaUrlsValue: message.mediaUrls,
          hasBody: !!message.body,
          bodyValue: message.body,
          allKeys: Object.keys(message),
          // STATUS DEBUG INFO
          hasStatus: !!message.status,
          statusValue: message.status,
          hasSentAt: !!message.sentAt,
          sentAtValue: message.sentAt,
          hasSendToTelnyx: !!message.sendToTelnyx,
          sendToTelnyxValue: message.sendToTelnyx,
          hasTelnyxResponse: !!message.telnyxResponse,
          telnyxResponseValue: message.telnyxResponse,
          hasTelnyxMessageId: !!message.telnyxMessageId,
          telnyxMessageIdValue: message.telnyxMessageId,
          hasToStatus: !!message.to?.status,
          toStatusValue: message.to?.status,
          // WEBHOOK STATUS FIELDS
          hasCompletedAt: !!message.completedAt,
          completedAtValue: message.completedAt,
          hasErrorCode: !!message.errorCode,
          errorCodeValue: message.errorCode,
          hasLastStatusUpdate: !!message.lastStatusUpdate,
          lastStatusUpdateValue: message.lastStatusUpdate,
          hasWebhookPayload: !!message.telnyxStatusPayload,
          webhookPayloadValue: message.telnyxStatusPayload,
          fullMessage: message
        });
      }
    }

    if (!mediaUrls || mediaUrls.length === 0) {
      return null;
    }

    return (
      <div className="mt-2 space-y-2">
        {mediaUrls.map((url, index) => {
          if (!url || typeof url !== 'string') {
            return null;
          }
          
          // Determine media type from URL or file extension
          const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i) || 
                         url.includes('image') || 
                         (url.includes('firebasestorage.googleapis.com') && !url.includes('video'));
          const isVideo = url.match(/\.(mp4|mov|avi|webm|quicktime)(\?|$)/i) || 
                         url.includes('video') ||
                         (url.includes('firebasestorage.googleapis.com') && url.includes('video'));

          console.log(`Rendering media ${index + 1}:`, { 
            url, 
            isImage, 
            isVideo,
            fileExtension: url.match(/\.([^.?]+)(\?|$)/i)?.[1],
            containsVideo: url.includes('video'),
            isFirebaseStorage: url.includes('firebasestorage.googleapis.com')
          });

          if (isImage) {
            return (
              <div key={index} className="relative group">
                <img
                  src={url}
                  alt="MMS attachment"
                  className="max-w-[200px] sm:max-w-[250px] w-auto h-auto rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => window.open(url, '_blank')}
                  onLoad={() => console.log(`Image loaded successfully: ${url}`)}
                  onError={(e) => {
                    console.error(`Failed to load image: ${url}`, e);
                    e.target.style.display = 'none';
                  }}
                />
                {/* Click hint overlay */}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center pointer-events-none">
                  <div className="bg-black/50 text-white px-2 py-1 rounded text-xs">
                    Click to expand
                  </div>
                </div>
              </div>
            );
          } else if (isVideo) {
            return (
              <div key={index} className="relative group">
                <video
                  controls
                  preload="metadata"
                  className="max-w-[200px] sm:max-w-[250px] w-auto h-auto rounded-lg shadow-md cursor-pointer"
                  onLoadedData={() => console.log(`Video loaded successfully: ${url}`)}
                  onError={(e) => {
                    console.error(`Failed to load video: ${url}`, e);
                    e.target.style.display = 'none';
                  }}
                  onClick={(e) => {
                    // Allow normal video controls, but also enable opening in new tab on double-click
                    e.stopPropagation();
                  }}
                  onDoubleClick={() => window.open(url, '_blank')}
                >
                  <source src={url} />
                  Your browser does not support the video tag.
                </video>
                {/* Video overlay hint */}
                <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  Double-click to open
                </div>
              </div>
            );
          } else {
            // Try to render as video first, then image, then generic attachment
            return (
              <div key={index} className="relative">
                <video
                  controls
                  preload="metadata"
                  className="max-w-[200px] sm:max-w-[250px] w-auto h-auto rounded-lg shadow-md cursor-pointer"
                  onLoadedData={() => {
                    console.log(`Fallback video loaded successfully: ${url}`);
                  }}
                  onError={(e) => {
                    console.log(`Not a video, trying as image: ${url}`);
                    // Replace with image attempt
                    e.target.outerHTML = `
                      <img src="${url}" alt="MMS attachment" 
                           class="max-w-[200px] sm:max-w-[250px] w-auto h-auto rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                           onclick="window.open('${url}', '_blank')"
                           onload="console.log('Fallback image loaded successfully: ${url}')"
                           onerror="this.outerHTML='<a href=\\"${url}\\" target=\\"_blank\\" rel=\\"noopener noreferrer\\" class=\\"inline-flex items-center px-3 py-2 text-sm bg-white/10 rounded-lg hover:bg-white/20 transition-colors\\"><svg class=\\"w-4 h-4 mr-2\\" fill=\\"currentColor\\" viewBox=\\"0 0 20 20\\"><path fill-rule=\\"evenodd\\" d=\\"M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H9a1 1 0 110-2H8.771l.823-.823a1 1 0 00-1.414-1.414l-2 2a1 1 0 000 1.414l2 2a1 1 0 001.414-1.414L8.771 12z\\" clip-rule=\\"evenodd\\"></path></svg>Media Attachment ${index + 1}</a>';">
                    `;
                  }}
                  onDoubleClick={() => window.open(url, '_blank')}
                >
                  <source src={url} />
                  Your browser does not support the video tag.
                </video>
              </div>
            );
          }
        })}
      </div>
    );
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    try {
      if (!timestamp) return '';
      
      let date;
      
      // Handle different timestamp formats
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp?.toDate) {
        // Firestore timestamp
        date = timestamp.toDate();
      } else if (typeof timestamp === 'string') {
        // Try parsing ISO string
        date = new Date(timestamp);
      } else if (timestamp?.seconds) {
        // Firestore timestamp with seconds
        date = new Date(timestamp.seconds * 1000);
      } else {
        return '';
      }
      
      // Validate the date
      if (isNaN(date.getTime())) {
        return '';
      }
      
      const now = new Date();
      
      // If today, show time only
      if (date.toDateString() === now.toDateString()) {
        return format(date, 'h:mm a');
      }
      
      // If within the last week, show day name and time
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      if (date > oneWeekAgo) {
        return format(date, 'EEEE h:mm a');
      }
      
      // Otherwise show date and time
      return format(date, 'MM/dd/yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return '';
    }
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = {};
    
    messages.forEach(message => {
      let date;
      
      // Handle Firestore timestamp
      if (message.timestamp?.seconds) {
        date = new Date(message.timestamp.seconds * 1000).toDateString();
      } 
      // Handle ISO string
      else if (message.received_at) {
        date = parseISO(message.received_at).toDateString();
      } 
      // Fallback
      else {
        date = 'Unknown Date';
      }
      
      if (!groups[date]) {
        groups[date] = [];
      }
      
      groups[date].push(message);
    });
    
    return groups;
  };

  // Format date for display in the message group header
  const formatDate = (dateString) => {
    // FALLBACK: return literal if unknown
    if (dateString === 'Unknown Date') {
      return dateString;
    }
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return format(date, 'MMMM d, yyyy');
    }
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="flex flex-col h-full">
      {/* Header - Fixed at top */}
      <div className="flex-none p-4 border-b border-white/10 flex justify-between items-center z-10 backdrop-blur-sm">
        <div className="flex items-center">
          <h2 className="text-lg font-medium text-white">
            {conversation ? (conversation.lastContactNumber ? 
              formatPhoneNumber(conversation.lastContactNumber) : 
              'New Conversation') : 
              'Select a conversation'}
          </h2>
          {conversation?.connectionStatus && (
            <span className={`ml-2 text-xs px-2 py-1 rounded-full border backdrop-blur-sm ${
              conversation.connectionStatus === 'active' 
                ? 'bg-emerald-400/20 text-emerald-400 border-emerald-400/30' 
                : 'bg-white/10 text-white/60 border-white/20'
            }`}>
              {conversation.connectionStatus === 'active' ? 'Active' : conversation.connectionStatus}
            </span>
          )}
        </div>
        
        {/* Navigation Buttons */}
        {conversation?.lastContactNumber && (
          <div className="flex gap-2">
            {/* Media Gallery Button */}
            <button
              onClick={() => setShowMediaGallery(true)}
              className="w-9 h-9 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white/60 hover:text-purple-400 hover:bg-purple-500/10 hover:border-purple-400/20 transition-all duration-200"
              title="Media Gallery"
            >
              <PhotoIcon className="w-4 h-4" />
            </button>
            
            {/* Contact Navigation Button */}
            <button
              onClick={() => onNavigateToContact && onNavigateToContact(conversation.lastContactNumber)}
              className="w-9 h-9 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white/60 hover:text-green-400 hover:bg-green-500/10 hover:border-green-400/20 transition-all duration-200"
              title="View in Contacts"
            >
              <MdContacts className="w-4 h-4" />
            </button>
            
            {/* Voice Dialer Navigation Button */}
            <button
              onClick={() => onNavigateToVoiceDialer && onNavigateToVoiceDialer(conversation.lastContactNumber)}
              className="w-9 h-9 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white/60 hover:text-blue-400 hover:bg-blue-500/10 hover:border-blue-400/20 transition-all duration-200"
              title="Call with Voice Dialer"
            >
              <GiSoundWaves className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      
      {/* Messages - Scrollable middle area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-8">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-500/20 border border-red-400/30 rounded-xl p-4 text-red-300 backdrop-blur-sm">
            <p>{error}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex justify-center items-center h-full text-white/60">
            {conversation?.lastContactNumber ? (
              <p>No messages in this conversation yet.</p>
            ) : (
              <p>Type a message to start the conversation.</p>
            )}
          </div>
        ) : (
          Object.keys(messageGroups).map(date => (
            <div key={date} className="space-y-4">
              <div className="flex justify-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-xs text-white/60 border border-white/10">
                  {formatDate(date)}
                </div>
              </div>
              
              <div className="space-y-3">
                {messageGroups[date].map(message => {
                  // Check if this is an AI assistant insight
                  if (message.type === 'ai_assistant_insight' || message.isAIInsight) {
                    return (
                      <div key={message.id} className="flex justify-center">
                        <div className="max-w-[90vw] sm:max-w-lg w-full">
                          {renderAIInsight(message)}
                          <div className="text-xs text-white/40 text-center mt-2">
                            {formatTimestamp(message.timestamp || message.received_at)}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // Regular message rendering
                  return (
                    <div
                      key={message.id}
                      className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85vw] sm:max-w-md rounded-xl px-4 py-2 backdrop-blur-sm border shadow-lg ${
                          message.direction === 'outbound'
                            ? 'bg-indigo-600/80 text-white border-indigo-500/30'
                            : 'bg-white/10 text-white border-white/20'
                        }`}
                      >
                        {(message.text || message.body) && (
                          <p className="whitespace-pre-wrap break-words">{message.text || message.body}</p>
                        )}
                        {renderMediaContent(message)}
                        <div
                          className={`text-xs mt-1 ${
                            message.direction === 'outbound' ? 'text-indigo-200' : 'text-white/60'
                          } text-right`}
                        >
                          {formatTimestamp(message.timestamp || message.received_at)}
                          {message.direction === 'outbound' && (
                            <span className="ml-2">
                              {(() => {
                                // Check multiple possible status sources in priority order
                                let status = null;
                                let statusSource = 'unknown';
                              
                              // 1. Check webhook status (most recent/accurate)
                              if (message.telnyxStatusPayload?.to?.[0]?.status) {
                                status = message.telnyxStatusPayload.to[0].status;
                                statusSource = 'webhook';
                              }
                              // 2. Check top-level status from webhook
                              else if (message.status) {
                                status = message.status;
                                statusSource = 'webhook-status';
                              }
                              // 3. Check Telnyx response status
                              else if (message.telnyxResponse?.data?.to?.[0]?.status) {
                                status = message.telnyxResponse.data.to[0].status;
                                statusSource = 'response';
                              }
                              // 4. Check UI compatibility field
                              else if (message.to?.status) {
                                status = message.to.status;
                                statusSource = 'ui-compat';
                              }
                              // 5. Fallback to sendToTelnyx result
                              else if (message.sendToTelnyx === 'success') {
                                status = 'sent';
                                statusSource = 'send-result';
                              }
                              else if (message.sendToTelnyx === 'failure') {
                                status = 'failed';
                                statusSource = 'send-result';
                              }
                              else {
                                status = 'pending';
                                statusSource = 'default';
                              }
                              
                              // Create tooltip with status source info
                              const getTooltip = (statusText) => {
                                let tooltip = statusText;
                                if (message.completedAt) {
                                  const completedTime = new Date(message.completedAt).toLocaleTimeString();
                                  tooltip += ` at ${completedTime}`;
                                }
                                if (message.errorCode) {
                                  tooltip += ` (Error: ${message.errorCode})`;
                                }
                                return tooltip;
                              };
                              
                              // Display appropriate status icon with enhanced tooltips
                              switch (status) {
                                case 'delivered':
                                  return <span className="text-blue-400" title={getTooltip('Delivered to phone')}>✓✓</span>;
                                case 'sent':
                                  return <span className="text-green-400" title={getTooltip('Sent to carrier')}>✓</span>;
                                case 'failed':
                                case 'delivery_failed':
                                case 'sending_failed':
                                  return <span className="text-red-400" title={getTooltip('Delivery failed')}>❌</span>;
                                case 'queued':
                                  return <span className="text-yellow-400" title={getTooltip('Queued for delivery')}>⏳</span>;
                                case 'sending':
                                  return <span className="text-yellow-400" title={getTooltip('Sending to carrier')}>📤</span>;
                                case 'delivery_unconfirmed':
                                  return <span className="text-orange-400" title={getTooltip('Delivery unconfirmed')}>❓</span>;
                                case 'pending':
                                default:
                                  return <span className="text-gray-400" title={getTooltip('Status unknown')}>⋯</span>;
                              }
                              })()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message input - Fixed at bottom */}
      <div className="flex-none border-t border-white/10 p-4 backdrop-blur-sm">
        {/* Media attachments preview */}
        {attachedMedia.length > 0 && (
          <div className="mb-3 p-3 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-center mb-2">
              <MdAttachFile className="w-4 h-4 text-white/60 mr-2" />
              <span className="text-sm text-white/60">Media Attachments ({attachedMedia.length})</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {attachedMedia.map((media) => (
                <div key={media.id} className="relative group">
                  {media.type === 'image' ? (
                    <img
                      src={media.previewUrl}
                      alt={media.name}
                      className={`w-16 h-16 object-cover rounded-lg border border-white/20 ${
                        media.uploading ? 'opacity-50' : ''
                      }`}
                    />
                  ) : (
                    <div className={`w-16 h-16 bg-white/10 rounded-lg border border-white/20 flex items-center justify-center ${
                      media.uploading ? 'opacity-50' : ''
                    }`}>
                      <MdVideocam className="w-6 h-6 text-white/60" />
                    </div>
                  )}
                  
                  {/* Upload indicator */}
                  {media.uploading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => removeAttachedMedia(media.id)}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MdClose className="w-3 h-3 text-white" />
                  </button>
                  
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-xs text-white p-1 rounded-b-lg">
                    <div className="truncate">
                      {media.uploading ? 'Uploading...' : media.name}
                    </div>
                    {media.size && (
                      <div className="text-white/60">
                        {media.size}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={sendMessage} className="flex space-x-2">
          {/* + Button for attachments */}
          <div className="relative" ref={attachmentMenuRef}>
            <button
              type="button"
              onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
              className="w-10 h-10 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all duration-200"
              disabled={!conversation || sending}
            >
              <PlusIcon className="w-5 h-5" />
            </button>
            
            {/* Attachment menu */}
            {showAttachmentMenu && (
              <div className="absolute bottom-12 left-0 bg-black/80 backdrop-blur-xl border border-white/20 rounded-xl p-2 min-w-[200px] shadow-xl">
                <button
                  type="button"
                  onClick={() => {
                    fileInputRef.current?.click();
                    setShowAttachmentMenu(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <MdImage className="w-4 h-4 mr-3" />
                  Photo & Video
                </button>
                <div className="border-t border-white/10 my-1"></div>
                <div className="px-3 py-2 text-xs text-white/60">
                  Supports JPG, PNG, MP4, MOV (max 10MB)
                </div>
              </div>
            )}
          </div>

          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white/10 transition-all duration-200"
            placeholder="Type a message..."
            disabled={!conversation || sending}
          />
          <button
            type="submit"
            className="bg-indigo-600/80 backdrop-blur-sm rounded-xl px-8 py-2 min-w-[120px] text-white font-medium hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed border border-indigo-500/30 transition-all duration-200"
            disabled={!conversation || ((!newMessage.trim() && attachedMedia.length === 0) || sending)}
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </form>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileAttachment}
          className="hidden"
        />

        {error && (
          <p className="mt-2 text-sm text-red-400">{error}</p>
        )}
      </div>

      {/* Media Gallery Modal */}
      <MediaGallery
        messages={messages}
        isOpen={showMediaGallery}
        onClose={() => setShowMediaGallery(false)}
        conversation={conversation}
      />
    </div>
  );
}